/**
 * Theme resolution for the iframe app.
 *
 * The one structural difference from apps/web: this app does not OWN the
 * preference and does not persist it. The customer's site decides how the widget
 * looks — `data-omni-theme` on the script tag, updated later by a `theme`
 * message when their own dark-mode toggle flips. A widget that remembered its own
 * choice in storage would fight the page it is sitting on, and the visitor would
 * see a light panel on a dark site with no control anywhere that fixes it.
 *
 * `system` means "follow the VISITOR's OS preference". `auto` — the default
 * when the tag names no theme (P2-124, ADR-0248) — means "follow the SITE": the
 * frame cannot see the host page's colours, but the loader can, and it sends the
 * page background over the handshake. Until that arrives, and on a page whose
 * background is transparent or unreadable, `auto` falls back to the OS, which is
 * the only other shared signal.
 *
 * The other half of this story is the inline script in index.html, which has to
 * run before the first paint and therefore before this module exists. The two
 * copies are kept honest by theme.test.ts, which reads index.html off disk.
 */
export const THEME_PREFERENCES = ['light', 'dark', 'system', 'auto'] as const;
export type ThemePreference = (typeof THEME_PREFERENCES)[number];

/** What actually gets painted. `system` is a preference, never an outcome. */
export type EffectiveTheme = 'light' | 'dark';

/** Query parameter on the iframe's own URL. Must match index.html's inline script. */
export const THEME_QUERY_PARAM = 't';

export const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

export function isThemePreference(value: unknown): value is ThemePreference {
  return typeof value === 'string' && (THEME_PREFERENCES as readonly string[]).includes(value);
}

/**
 * Anything unrecognised becomes `auto` — a widget must never fail to render over
 * a typo, and a tag that names no theme the widget understands has expressed no
 * wish the site's own look should override.
 */
export function toThemePreference(value: unknown): ThemePreference {
  return isThemePreference(value) ? value : 'auto';
}

/**
 * @param pageIsDark the host page's background, as the loader reported it; null
 *   while unknown, which makes `auto` behave as `system`.
 */
export function resolveEffectiveTheme(
  preference: ThemePreference,
  systemPrefersDark: boolean,
  pageIsDark: boolean | null = null,
): EffectiveTheme {
  if (preference === 'auto' && pageIsDark !== null) {
    return pageIsDark ? 'dark' : 'light';
  }
  if (preference === 'system' || preference === 'auto') {
    return systemPrefersDark ? 'dark' : 'light';
  }
  return preference;
}

export interface ThemeTarget {
  readonly dataset: DOMStringMap;
  readonly style: Pick<CSSStyleDeclaration, 'colorScheme'>;
}

/**
 * Writes the theme onto `<html>`. `colorScheme` is not decoration: it is what
 * makes the UA paint scrollbars, form controls and the canvas behind the body in
 * the right mode — the part no CSS variable can reach.
 */
export function applyTheme(
  root: ThemeTarget,
  preference: ThemePreference,
  systemPrefersDark: boolean,
  pageIsDark: boolean | null = null,
): EffectiveTheme {
  const effective = resolveEffectiveTheme(preference, systemPrefersDark, pageIsDark);
  root.dataset['theme'] = effective;
  root.dataset['themePreference'] = preference;
  root.style.colorScheme = effective;
  return effective;
}
